import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X, FileUp, Camera, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SingleClauseDecoder } from "@/components/SingleClauseDecoder";

const SAMPLES = [
  { id: "lease", emoji: "🏠", name: "Lease Agreement" },
  { id: "nda", emoji: "📝", name: "NDA" },
  { id: "employment", emoji: "💼", name: "Employment Contract" },
] as const;

type SampleId = (typeof SAMPLES)[number]["id"];

const TRUST = [
  { emoji: "🔒", label: "Processed locally" },
  { emoji: "⚡", label: "Results in 60s" },
  { emoji: "🗑️", label: "Auto-deleted in 24h" },
  { emoji: "🌍", label: "Any jurisdiction" },
];

const SCAN_MESSAGES = [
  "Scanning page 3…",
  "Identifying clauses…",
  "Cross-referencing state laws…",
  "Flagging risky terms…",
];

export function HeroUpload() {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [activeSample, setActiveSample] = useState<SampleId | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scanIdx, setScanIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const ACCEPTED = [".pdf"];
  const MAX_BYTES = 15 * 1024 * 1024;

  // Animate progress + cycle micro-copy while uploading
  useEffect(() => {
    if (!uploading) return;
    setProgress(0);
    setScanIdx(0);
    const start = Date.now();
    const total = 2000;
    const tick = window.setInterval(() => {
      const pct = Math.min(100, Math.round(((Date.now() - start) / total) * 100));
      setProgress(pct);
      if (pct >= 100) window.clearInterval(tick);
    }, 60);
    const cycle = window.setInterval(() => {
      setScanIdx((i) => (i + 1) % SCAN_MESSAGES.length);
    }, 700);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(cycle);
    };
  }, [uploading]);

  function scrollToAnalyzer() {
    document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleFile(file: File | undefined | null) {
    if (!file) return;
    const lower = file.name.toLowerCase();
    const okType = ACCEPTED.some((ext) => lower.endsWith(ext)) || file.type.startsWith("image/");
    if (!okType) {
      toast.error("Unsupported file. Upload a PDF, DOCX, or image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File too large (15MB max).");
      return;
    }
    // Stash the file in case ContractAnalyzer hasn't mounted yet (lazy chunk).
    (window as any).__legaldPendingFile = file;
    scrollToAnalyzer();
    // Dispatch a few times to cover the lazy-mount race.
    const dispatch = () =>
      window.dispatchEvent(new CustomEvent("legald:upload", { detail: { file } }));
    dispatch();
    window.setTimeout(dispatch, 250);
    window.setTimeout(dispatch, 800);
  }

  function triggerSample(id: SampleId) {
    setActiveSample(id);
    setUploading(true);
    window.setTimeout(() => {
      setUploading(false);
      setModalOpen(true);
    }, 2000);
  }

  function closeModal() {
    setModalOpen(false);
    window.setTimeout(() => {
      setActiveSample(null);
    }, 200);
  }

  const sampleName = SAMPLES.find((s) => s.id === activeSample)?.name;

  return (
    <section
      className="relative"
      style={{
        background: "linear-gradient(180deg, #EEF2FF 0%, #F9FAFB 100%)",
        padding: "80px 16px 60px",
      }}
    >
      <div className="mx-auto max-w-5xl text-center">
        {/* Top badge */}
        <div
          className="inline-flex items-center gap-2 rounded-full border bg-white"
          style={{
            borderColor: "#F5A623",
            color: "#92400E",
            padding: "6px 16px",
            fontSize: 14,
          }}
        >
          🏆 Product Hunt #1 · 10,000+ documents analyzed
        </div>

        {/* Headline */}
        <h1
          className="mx-auto mt-6 max-w-3xl"
          style={{
            fontSize: "clamp(28px, 4.4vw, 42px)",
            lineHeight: 1.15,
            fontWeight: 600,
            color: "#374151",
            marginBottom: 12,
          }}
        >
          Understand Any Legal Document
          <br />
          <span
            className="relative inline-block"
            style={{
              fontSize: "clamp(44px, 7.4vw, 72px)",
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              backgroundImage: "linear-gradient(135deg, #1B3A6B 0%, #2563EB 55%, #3B82F6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              padding: "0 0.05em",
              marginTop: "0.15em",
            }}
          >
            in Plain English
          </span>
        </h1>

        <p
          className="mx-auto max-w-2xl"
          style={{ fontSize: 18, color: "#6B7280", marginBottom: 40 }}
        >
          Upload your contract → Get instant AI analysis → Know exactly what you're signing
        </p>

        {/* Upload zone */}
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-center">
          <div
            className={cn(
              "dropzone bg-white text-left w-full",
              dragOver && "is-dragover",
              uploading && "is-uploading",
              !dragOver && !uploading && "is-idle",
            )}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (uploading) return;
              const file = e.dataTransfer?.files?.[0];
              if (file) {
                setUploading(true);
                window.setTimeout(() => {
                  setUploading(false);
                  handleFile(file);
                }, 2000);
              }
            }}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <ProgressRing percent={progress} />
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                  {activeSample ? `Analyzing ${sampleName}…` : "Processing your document…"}
                </div>
                <div
                  key={scanIdx}
                  className="scan-line"
                  style={{ fontSize: 13, color: "#1B3A6B", fontWeight: 500 }}
                >
                  {SCAN_MESSAGES[scanIdx]}
                </div>
              </div>
            ) : (
              <>
                {/* Desktop / tablet view */}
                <div className="hidden sm:flex flex-col items-center gap-4 text-center">
                  <div className="dropzone__icon" aria-hidden>
                    <FileText className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>
                    {dragOver ? "Release to upload" : "Drop your document here"}
                  </div>
                  <div style={{ fontSize: 14, color: "#6B7280" }}>
                    PDF, DOCX · Any size · 80+ document types
                  </div>
                  <div style={{ height: 4 }} />
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    style={{
                      background: "#1B3A6B",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "14px 32px",
                      fontSize: 16,
                      fontWeight: 600,
                    }}
                  >
                    Upload Document →
                  </button>
                  <div style={{ fontSize: 13, color: "#6B7280" }}>
                    Or try a sample:{" "}
                    {SAMPLES.map((s, i) => (
                      <span key={s.id}>
                        <button
                          type="button"
                          onClick={() => triggerSample(s.id)}
                          className="underline underline-offset-2"
                          style={{ color: "#1B3A6B", fontWeight: 500 }}
                        >
                          {s.name}
                        </button>
                        {i < SAMPLES.length - 1 && <span> · </span>}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Mobile variant: compact two-button row */}
                <div className="flex sm:hidden flex-col gap-3 w-full">
                  <div
                    className="text-center"
                    style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}
                  >
                    Analyze a document
                  </div>
                  <div
                    className="text-center"
                    style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}
                  >
                    PDF, DOCX · 80+ types
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => inputRef.current?.click()}
                      className="mobile-btn"
                      style={{ background: "#1B3A6B", color: "#fff" }}
                    >
                      <FileUp className="h-4 w-4" /> Upload File
                    </button>
                    <button
                      type="button"
                      onClick={() => cameraRef.current?.click()}
                      className="mobile-btn"
                      style={{
                        background: "#fff",
                        color: "#1B3A6B",
                        border: "1.5px solid #1B3A6B",
                      }}
                    >
                      <Camera className="h-4 w-4" /> Take Photo
                    </button>
                  </div>
                  <div
                    className="text-center"
                    style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}
                  >
                    Or try:{" "}
                    {SAMPLES.map((s, i) => (
                      <span key={s.id}>
                        <button
                          type="button"
                          onClick={() => triggerSample(s.id)}
                          className="underline underline-offset-2"
                          style={{ color: "#1B3A6B", fontWeight: 500 }}
                        >
                          {s.name}
                        </button>
                        {i < SAMPLES.length - 1 && <span> · </span>}
                      </span>
                    ))}
                  </div>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf,.pdf,image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </>
            )}
          </div>
        </div>

        {/* Trust bar */}
        <ul
          className="mx-auto mt-8 grid grid-cols-2 justify-items-center gap-4 sm:flex sm:flex-wrap sm:justify-center sm:gap-8"
          style={{ fontSize: 13, color: "#6B7280", maxWidth: 720 }}
        >
          {TRUST.map((t) => (
            <li key={t.label} className="flex items-center gap-2">
              <span aria-hidden>{t.emoji}</span>
              <span>{t.label}</span>
            </li>
          ))}
        </ul>

        {/* CTA below trust bar */}
        <div className="mt-6">
          <Link
            to="/auth"
            style={{ color: "#1B3A6B", fontWeight: 600, fontSize: 15 }}
            className="underline-offset-4 hover:underline"
          >
            Try Free — No credit card required
          </Link>
        </div>

        {/* Single Clause Decoder — instant, no signup */}
        <SingleClauseDecoder />

        {/* Sample picker section */}
        <div className="mt-16">
          <p style={{ fontSize: 18, fontWeight: 600, color: "#6B7280", marginBottom: 24 }}>
            See how it works — try a sample
          </p>
          <div className="flex flex-col items-stretch justify-center gap-6 sm:flex-row sm:items-stretch">
            {SAMPLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => triggerSample(s.id)}
                className="sample-card group bg-white text-center transition-all"
                style={{
                  width: "100%",
                  maxWidth: 220,
                  padding: 20,
                  borderRadius: 12,
                  border: "1px solid #E5E7EB",
                  margin: "0 auto",
                }}
              >
                <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 8 }}>{s.emoji}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{s.name}</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>Click to analyze</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && <DemoModal sample={activeSample!} onClose={closeModal} />}

      <style>{`
        .sample-card:hover {
          box-shadow: 0 4px 6px rgba(0,0,0,0.07);
          border-color: #1B3A6B !important;
          cursor: pointer;
          transform: translateY(-2px);
        }

        /* === Dropzone === */
        .dropzone {
          max-width: 560px;
          padding: clamp(20px, 5vw, 48px);
          border-radius: 20px;
          border: 2px dashed #93C5FD;
          background: #fff;
          transition: border-color .25s ease, background-color .25s ease, transform .25s ease, box-shadow .25s ease;
          position: relative;
        }
        .dropzone.is-idle { animation: dz-pulse 2.5s ease-in-out infinite; }
        @keyframes dz-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(27, 58, 107, 0.0); border-color: #93C5FD; }
          50%      { box-shadow: 0 0 0 6px rgba(27, 58, 107, 0.06); border-color: #60A5FA; }
        }
        .dropzone.is-dragover {
          animation: none;
          border-style: solid;
          border-color: #1B3A6B;
          background-color: rgba(27, 58, 107, 0.08);
          transform: scale(1.01);
          box-shadow: 0 12px 28px -12px rgba(27,58,107,0.25);
        }
        .dropzone.is-dragover .dropzone__icon {
          transform: scale(1.1);
        }
        .dropzone.is-uploading {
          animation: none;
          border-style: solid;
          border-color: #1B3A6B;
        }
        .dropzone__icon {
          width: 72px;
          height: 72px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: rgba(27, 58, 107, 0.08);
          color: #1B3A6B;
          transition: transform .35s cubic-bezier(.34,1.56,.64,1);
        }

        .mobile-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 14px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
        }

        /* Scan message fade */
        .scan-line {
          animation: scan-fade .5s ease-out;
        }
        @keyframes scan-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .dropzone, .dropzone.is-idle, .scan-line, .dropzone__icon { animation: none !important; transition: none !important; }
        }
      `}</style>
    </section>
  );
}

function ProgressRing({ percent }: { percent: number }) {
  const size = 72;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#E5E7EB"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#1B3A6B"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset .15s linear" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 700,
          color: "#1B3A6B",
        }}
      >
        {percent}%
      </div>
    </div>
  );
}

const MOCK: Record<
  SampleId,
  {
    redFlags: { clause: string; explain: string }[];
    rights: string[];
    obligations: string[];
    qa: { q: string; a: string };
  }
> = {
  lease: {
    redFlags: [
      {
        clause: "Section 4.2: 'Landlord may terminate at any time'",
        explain:
          "This gives the landlord unrestricted termination rights, which is unusual and likely unenforceable.",
      },
      {
        clause: "Section 7.1: 'Tenant waives right to jury trial'",
        explain: "Forfeits an important legal protection in any future disputes.",
      },
    ],
    rights: ["Quiet enjoyment of the premises", "Return of security deposit within 30 days"],
    obligations: ["Pay rent by the 1st of each month", "Maintain renters insurance"],
    qa: {
      q: "What happens if I break the lease early?",
      a: "According to Section 4.2, early termination requires 60 days notice and forfeiture of one month's rent as a fee.",
    },
  },
  nda: {
    redFlags: [
      {
        clause: "Section 3: 'Perpetual confidentiality'",
        explain:
          "Most NDAs cap confidentiality at 2–5 years; perpetual obligations are extremely unusual.",
      },
      {
        clause: "Section 6: 'Liquidated damages of $250,000'",
        explain: "Pre-set damages this high are punitive and may be unenforceable in many states.",
      },
    ],
    rights: ["Use information for the stated purpose", "Disclose if legally compelled"],
    obligations: ["Protect information with reasonable care", "Return materials upon request"],
    qa: {
      q: "Can I work for a competitor after signing?",
      a: "Yes — this is an NDA, not a non-compete. It restricts what you share, not where you work.",
    },
  },
  employment: {
    redFlags: [
      {
        clause: "Section 12: 'Non-compete for 2 years, nationwide'",
        explain:
          "Overly broad non-competes are unenforceable in CA, ND, OK and increasingly limited elsewhere.",
      },
      {
        clause: "Section 9: 'All inventions assigned, including off-hours work'",
        explain:
          "Many states require a carve-out for work done on personal time without company resources.",
      },
    ],
    rights: ["Stated salary and bonus structure", "PTO accrual per company policy"],
    obligations: ["At-will employment standards", "Confidentiality of trade secrets"],
    qa: {
      q: "Can my employer change my role without notice?",
      a: "Section 2 reserves the right to modify duties, but compensation changes require 30 days written notice.",
    },
  },
};

function DemoModal({ sample, onClose }: { sample: SampleId; onClose: () => void }) {
  const data = MOCK[sample];
  const name = SAMPLES.find((s) => s.id === sample)?.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-y-auto bg-white animate-fade-in-up"
        style={{ borderRadius: 16, padding: "clamp(24px, 4vw, 40px)", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-1.5 text-foreground hover:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>

        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Sample analysis
        </div>
        <h3
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: "#111827",
            marginTop: 4,
            marginBottom: 24,
          }}
        >
          {name}
        </h3>

        <div className="grid gap-6 md:grid-cols-3 stagger-children">
          {/* Red flags */}
          <div>
            <div style={{ color: "#DC2626", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
              🚩 Red Flags Found
            </div>
            <ul className="space-y-3">
              {data.redFlags.map((rf, i) => (
                <li key={i} style={{ fontSize: 13, lineHeight: 1.5 }}>
                  <div
                    style={{
                      background: "#FEE2E2",
                      color: "#991B1B",
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontWeight: 600,
                    }}
                  >
                    ⚠️ {rf.clause}
                  </div>
                  <p style={{ color: "#6B7280", marginTop: 6 }}>{rf.explain}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Rights & Obligations */}
          <div>
            <div style={{ color: "#1B3A6B", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
              📋 Your Rights & Obligations
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#15803D", marginBottom: 6 }}>
              Your Rights
            </div>
            <ul
              className="space-y-1.5"
              style={{ fontSize: 13, color: "#111827", marginBottom: 12 }}
            >
              {data.rights.map((r, i) => (
                <li key={i}>✓ {r}</li>
              ))}
            </ul>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#1B3A6B", marginBottom: 6 }}>
              Your Obligations
            </div>
            <ul className="space-y-1.5" style={{ fontSize: 13, color: "#111827" }}>
              {data.obligations.map((r, i) => (
                <li key={i}>• {r}</li>
              ))}
            </ul>
          </div>

          {/* Ask AI */}
          <div>
            <div style={{ color: "#7C3AED", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
              💬 Ask AI About This Document
            </div>
            <div
              style={{
                background: "#F5F3FF",
                padding: 12,
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <p style={{ color: "#1B3A6B", fontWeight: 600 }}>Q: {data.qa.q}</p>
              <p style={{ color: "#374151", marginTop: 6 }}>A: {data.qa.a}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/auth"
            className="inline-block"
            style={{
              background: "#1B3A6B",
              color: "#fff",
              padding: "12px 28px",
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Want to analyze your own document? Try Free →
          </Link>
          <p style={{ fontSize: 12, color: "#6B7280", marginTop: 10 }}>No credit card required</p>
        </div>
      </div>
    </div>
  );
}
